import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Typography, Box, FormControl, InputLabel, Select, MenuItem,
    CircularProgress, Alert, Stack, FormHelperText
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import api from '../utils/Api';

const UploadSchoolSbaModal = ({ open, onClose, school }) => {
    const [classLevelKey, setClassLevelKey] = useState('');
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setError(null);
        setSuccess(null);
    };

    const handleUpload = async () => {
        if (!classLevelKey) {
            setError("Please select a class level.");
            return;
        }
        if (!file) {
            setError("Please select a file to upload.");
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            const formData = new FormData();
            formData.append('file', file);
            formData.append('schoolId', school._id);
            formData.append('classLevelKey', classLevelKey);

            const res = await api.post('/sba/admin/upload-school-template', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setSuccess(res.data.message || "Template uploaded successfully!");
            setFile(null);
            
            setTimeout(() => {
                onClose();
            }, 2000);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || "Failed to upload template.");
        } finally {
            setLoading(false);
        }
    };

    const standardKeys = [
        { value: 'CRECHE', label: 'Creche / Daycare' },
        { value: 'NURSERY_1', label: 'Nursery 1' },
        { value: 'NURSERY_2', label: 'Nursery 2' },
        { value: 'KG_1', label: 'KG 1' },
        { value: 'KG_2', label: 'KG 2' },
        { value: 'BASIC_1', label: 'Basic 1 (Primary 1)' },
        { value: 'BASIC_2', label: 'Basic 2 (Primary 2)' },
        { value: 'BASIC_3', label: 'Basic 3 (Primary 3)' },
        { value: 'BASIC_4', label: 'Basic 4 (Primary 4)' },
        { value: 'BASIC_5', label: 'Basic 5 (Primary 5)' },
        { value: 'BASIC_6', label: 'Basic 6 (Primary 6)' },
        { value: 'BASIC_7', label: 'Basic 7 (JHS 1)' },
        { value: 'BASIC_8', label: 'Basic 8 (JHS 2)' },
        { value: 'BASIC_9', label: 'Basic 9 (JHS 3)' }
    ];

    return (
        <Dialog open={open} onClose={loading ? null : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Upload Custom SBA for {school?.name}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Upload a specific blank master SBA template for a chosen class level. This will override the global template for this school.
                    </Typography>

                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}

                    <FormControl fullWidth size="small">
                        <InputLabel>Class Level</InputLabel>
                        <Select
                            value={classLevelKey}
                            label="Class Level"
                            onChange={(e) => setClassLevelKey(e.target.value)}
                        >
                            {standardKeys.map(k => (
                                <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>Select the level this template applies to</FormHelperText>
                    </FormControl>

                    <Box sx={{ border: '1px dashed grey', p: 2, textAlign: 'center', borderRadius: 1 }}>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            id="sba-upload"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                        <label htmlFor="sba-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<UploadFile />}
                                disabled={loading}
                            >
                                Select Master Excel File
                            </Button>
                        </label>
                        {file && (
                            <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
                                Selected: {file.name}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    color="primary"
                    disabled={loading || !file || !classLevelKey}
                    startIcon={loading ? <CircularProgress size={20} /> : <UploadFile />}
                >
                    {loading ? "Uploading..." : "Upload Template"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UploadSchoolSbaModal;
